import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PRIMARY_NAV } from '../utils/primaryNav';
import './MobileTabBar.css';

const ICONS: Record<string, React.ReactNode> = {
    map: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
            <path d="M9 4v14M15 6v14" />
        </svg>
    ),
    leagues: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
            <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
            <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
            <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
        </svg>
    ),
    play: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
            <path d="M16 6h3v1.5A3.5 3.5 0 0 1 15.5 11M8 6H5v1.5A3.5 3.5 0 0 0 8.5 11M12 13v4M8 20h8" />
        </svg>
    ),
    county: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" />
            <circle cx="12" cy="9.5" r="2.5" />
        </svg>
    ),
    news: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <rect x="3" y="4" width="15" height="16" rx="2" />
            <path d="M18 8h3v10a2 2 0 0 1-2 2M7 8h7M7 12h7M7 16h4" />
        </svg>
    ),
};

const HIDDEN_PREFIXES = ['/discord', '/admin', '/login', '/register', '/p'];

/** Immersive map home — no bottom tabs competing with the map. */
const HIDDEN_EXACT = ['/'];

const MobileTabBar: React.FC = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();

    const hidden =
        HIDDEN_EXACT.includes(pathname) ||
        HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

    useEffect(() => {
        document.body.classList.toggle('has-mobile-tabbar', !hidden);
        return () => document.body.classList.remove('has-mobile-tabbar');
    }, [hidden]);

    if (hidden) return null;

    // Map-first: only Map + Play (attic portal) on mobile; rest via More / Play hub.
    const tabs = PRIMARY_NAV.filter((item) => item.id === 'map' || item.id === 'play');

    return (
        <nav className="mobile-tabbar" aria-label={t('nav.primaryMobile', { defaultValue: 'Primary' })}>
            {tabs.map((item) => {
                const to = item.path || '/';
                const active = item.match(pathname);
                return (
                    <Link
                        key={item.id}
                        to={to}
                        className={`mobile-tabbar-item${active ? ' is-active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                    >
                        <span className="mobile-tabbar-icon">{ICONS[item.id]}</span>
                        <span className="mobile-tabbar-label">
                            {t(item.labelKey, { defaultValue: item.defaultLabel })}
                        </span>
                    </Link>
                );
            })}
        </nav>
    );
};

export default MobileTabBar;
